import React, { useCallback, useState, useRef, useEffect } from 'react';
import { useAccessibility } from '../../context/AccessibilityContext/AccessibilityContext';
import styles from './teammatecard.module.scss';
import teammate from '../../assets/teammate.jpg';

const TeammateCard = React.memo(({ 
  width = '240px', 
  image = teammate, 
  sport = 'Не указано', 
  prizes = 'Не указано', 
  experience = 'Не указано',
  name = 'Сотрудник',
  position = '',
  description = '',
  achievements = [],
  onClick
}) => {
  const { settings: { contrast, fontSize } } = useAccessibility();
  const [imageOrientation, setImageOrientation] = useState('unknown');
  const [imageLoaded, setImageLoaded] = useState(false);
  const imgRef = useRef(null);

  // Функция для определения ориентации с учетом соотношения сторон
  const detectOrientation = useCallback((imgElement) => {
    const width = imgElement.naturalWidth;
    const height = imgElement.naturalHeight;
    const ratio = width / height;
    
    // Точное определение ориентации
    if (ratio > 1.2) {
      return 'landscape'; // Сильно шире, чем выше (альбомная)
    } else if (ratio < 0.8) {
      return 'portrait'; // Сильно выше, чем шире (портретная)
    } else {
      return 'square'; // Приблизительно квадратное
    }
  }, []);

  // Memoize the error handler
  const handleImageError = useCallback((e) => {
    console.error('Image failed to load:', image);
    e.target.src = teammate;
    e.target.alt = 'Фотография сотрудника недоступна';
    setImageOrientation('unknown');
    setImageLoaded(true);
  }, [image]);

  // Handle image load to detect orientation
  const handleImageLoad = useCallback((e) => {
    const img = e.target;
    try {
      const orientation = detectOrientation(img);
      setImageOrientation(orientation);
      setImageLoaded(true);
    } catch (error) {
      console.error('Error detecting orientation:', error);
      setImageOrientation('unknown');
      setImageLoaded(true);
    }
  }, [detectOrientation]);

  // Use effect to preload image and detect orientation
  useEffect(() => {
    if (!image || image === teammate) {
      setImageOrientation('unknown');
      setImageLoaded(true);
      return;
    }

    const img = new Image();
    img.src = image;
    
    img.onload = () => {
      try {
        const orientation = detectOrientation(img);
        setImageOrientation(orientation);
        setImageLoaded(true);
      } catch (error) {
        console.error('Error preloading image:', error);
        setImageOrientation('unknown');
        setImageLoaded(true);
      }
    };
    
    img.onerror = () => {
      console.error('Failed to preload image:', image);
      setImageOrientation('unknown');
      setImageLoaded(true);
    };
  }, [image, detectOrientation]);

  // Calculate dynamic styles
  const cardStyle = {
    width,
    '--card-bg': contrast !== 'default' ? 'var(--bg-primary)' : '#fff',
    '--card-text': contrast !== 'default' ? 'var(--text-primary)' : '#272b37',
    '--font-size-multiplier': fontSize === 'large' ? '1.1' : '1',
  };

  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick?.();
    }
  }, [onClick]);

  // Format experience to max 20 characters
  const formatExperience = (exp) => {
    if (!exp || exp === 'Не указано') return exp;
    
    const trimmed = exp.trim();
    if (trimmed.length <= 20) return trimmed;
    
    return trimmed.substring(0, 17) + '...';
  };

  // Prepare teammate data for modal
  const teammateData = {
    name,
    sport,
    prizes,
    experience,
    image,
    position,
    description,
    achievements
  };

  // Determine CSS class based on orientation
  const getOrientationClass = () => {
    if (!imageLoaded) return styles.unknownImage;
    
    switch(imageOrientation) {
      case 'landscape':
        return styles.landscapeImage;
      case 'portrait':
        return styles.portraitImage;
      case 'square':
        return styles.squareImage;
      default:
        return styles.unknownImage;
    }
  };

  // Get specific image style based on orientation
  const getImageStyle = () => {
    if (!imageLoaded) return {};
    
    switch(imageOrientation) {
      case 'portrait':
        // Для портретных фото - фокусируемся на верхней части (где обычно лицо)
        return { objectPosition: 'top center' };
      case 'landscape':
        // Для альбомных - центр
        return { objectPosition: 'center center' };
      case 'square':
        // Для квадратных - центр
        return { objectPosition: 'center center' };
      default:
        return { objectPosition: 'center center' };
    }
  };

  const cardClassName = `${styles.teammateCard} ${getOrientationClass()}`;

  return (
    <article
      className={cardClassName}
      style={cardStyle}
      aria-label={`Информация о ${name}`}
      role={onClick ? 'button' : 'article'}
      tabIndex={onClick ? 0 : -1}
      onClick={() => onClick?.(teammateData)}
      onKeyDown={onClick ? handleKeyPress : undefined}
      data-orientation={imageOrientation}
      data-loaded={imageLoaded}
    >
      <div className={styles.wrapper}>
        <div className={styles.imageContainer}>
          <div className={styles.imageWrapper}>
            <img 
              ref={imgRef}
              className={styles.image}
              src={image}
              alt={name ? `Фотография ${name}` : 'Фотография сотрудника'}
              loading="lazy"
              decoding="async"
              onError={handleImageError}
              onLoad={handleImageLoad}
              style={getImageStyle()}
            />
            {!imageLoaded && (
              <div className={styles.imageLoader}>
                <div className={styles.loaderSpinner}></div>
              </div>
            )}
          </div>
        </div>
        <h3 className={styles.name}>{name}</h3>
        {position && <div className={styles.position}>{position}</div>}
        <div className={styles.details}>
          <span className={styles.sport}>
            <strong>Вид спорта:</strong> {sport}
          </span>
          <span className={styles.prizes}>
            <strong>Звания и награды:</strong> {prizes}
          </span>
          <span className={styles.exp}>
            <strong>Трудовой стаж:</strong> {formatExperience(experience)}
          </span>
        </div>
      </div>
    </article>
  );
});

TeammateCard.displayName = 'TeammateCard';

export default TeammateCard;